import React, { useEffect, useState } from "react";
import { Alert, Button, Form, ListGroup, Modal, Spinner } from "react-bootstrap";
import api from "../api";

const ProjectLeaderManagementModal = ({ show, onHide, project, onUpdated }) => {
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!show || !project) return;
    setForm({
      description: project.description || "",
      role: project.participantTypes?.[0]?.role || "",
      additionalRequirements:
        project.participantTypes?.[0]?.additionalRequirements || "",
      minimum: project.weeklyHoursRequired?.minimum ?? 0,
      maximum: project.weeklyHoursRequired?.maximum ?? 0,
      transportProvided: Boolean(project.location?.transportProvided),
      transportDetails: project.location?.transportDetails || "",
    });
    setMessage(null);
    setError(null);

    setLoading(true);
    Promise.all([
      api.get(`/projects/${project._id}/participants`),
      api.get(`/participations/${project._id}/join-requests`),
    ])
      .then(([membersResponse, requestsResponse]) => {
        setMembers(membersResponse.data || []);
        setRequests(requestsResponse.data || []);
      })
      .catch((requestError) =>
        setError(requestError.response?.data?.error || "Unable to load project management data.")
      )
      .finally(() => setLoading(false));
  }, [show, project]);

  const submitAmendment = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const location = project.location
        ? {
            ...project.location,
            transportProvided: form.transportProvided,
            transportDetails: form.transportProvided ? form.transportDetails : "",
          }
        : project.location;
      const response = await api.patch(`/projects/${project._id}`, {
        description: form.description,
        weeklyHoursRequired: {
          minimum: Number(form.minimum),
          maximum: Number(form.maximum),
        },
        participantTypes: [
          {
            ...(project.participantTypes?.[0] || {}),
            role: form.role,
            additionalRequirements: form.additionalRequirements,
          },
        ],
        ...(location ? { location } : {}),
      });
      setMessage(response.data?.message || "Project details updated successfully.");
      onUpdated?.();
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Unable to submit project changes.");
    } finally {
      setSaving(false);
    }
  };

  const reviewRequest = async (participationId, status) => {
    try {
      await api.patch(
        `/participations/${project._id}/join-requests/${participationId}`,
        { status }
      );
      setRequests((current) => current.filter((item) => item._id !== participationId));
      setMessage(`Join request ${status}.`);
      if (status === "approved") {
        const response = await api.get(`/projects/${project._id}/participants`);
        setMembers(response.data || []);
      }
      onUpdated?.();
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Unable to review join request.");
    }
  };

  const changeRole = async (member) => {
    const role = member.role === "leader" ? "member" : "leader";
    try {
      await api.patch(
        `/participations/${project._id}/members/${member._id}/role`,
        { role }
      );
      setMembers((current) =>
        current.map((item) => (item._id === member._id ? { ...item, role } : item))
      );
      setMessage(`Member is now a project ${role}.`);
      onUpdated?.();
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Unable to change the member role.");
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Manage {project?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message && <Alert variant="success">{message}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : (
          <>
            <h5>Project amendments</h5>
            <p className="text-muted small">
              Changes to an approved project are submitted to an administrator before becoming public.
            </p>
            <Form onSubmit={submitAmendment}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={3} value={form.description || ""} onChange={(event) => setForm({ ...form, description: event.target.value })} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Volunteer role</Form.Label>
                <Form.Control value={form.role || ""} onChange={(event) => setForm({ ...form, role: event.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Additional requirements</Form.Label>
                <Form.Control as="textarea" value={form.additionalRequirements || ""} onChange={(event) => setForm({ ...form, additionalRequirements: event.target.value })} />
              </Form.Group>
              <div className="d-flex gap-3 mb-3">
                <Form.Group className="flex-grow-1"><Form.Label>Minimum weekly hours</Form.Label><Form.Control type="number" min="0" value={form.minimum ?? 0} onChange={(event) => setForm({ ...form, minimum: event.target.value })} /></Form.Group>
                <Form.Group className="flex-grow-1"><Form.Label>Maximum weekly hours</Form.Label><Form.Control type="number" min="0" value={form.maximum ?? 0} onChange={(event) => setForm({ ...form, maximum: event.target.value })} /></Form.Group>
              </div>
              {project?.locationType !== "Online" && (
                <>
                  <Form.Check className="mb-2" label="Transport provided" checked={Boolean(form.transportProvided)} onChange={(event) => setForm({ ...form, transportProvided: event.target.checked })} />
                  {form.transportProvided && <Form.Control className="mb-3" placeholder="Transport details" value={form.transportDetails || ""} onChange={(event) => setForm({ ...form, transportDetails: event.target.value })} />}
                </>
              )}
              <Button type="submit" disabled={saving}>{saving ? "Submitting..." : "Submit changes"}</Button>
            </Form>

            <hr className="my-4" />
            <h5>Pending join requests</h5>
            {requests.length === 0 ? <p className="text-muted small">No pending requests.</p> : (
              <ListGroup className="mb-4">{requests.map((request) => <ListGroup.Item key={request._id} className="d-flex justify-content-between align-items-center"><span>{request.student?.name} {request.student?.surname} <small className="text-muted">{request.student?.studentNumber}</small></span><span><Button size="sm" variant="success" className="me-2" onClick={() => reviewRequest(request._id, "approved")}>Approve</Button><Button size="sm" variant="outline-danger" onClick={() => reviewRequest(request._id, "rejected")}>Reject</Button></span></ListGroup.Item>)}</ListGroup>
            )}

            <h5>Project leaders and members</h5>
            <ListGroup>{members.map((member) => <ListGroup.Item key={member._id} className="d-flex justify-content-between align-items-center"><span>{member.student?.organizationName || `${member.student?.name || ""} ${member.student?.surname || ""}`.trim()} <strong className="ms-2">{member.role}</strong></span><Button size="sm" variant="outline-primary" onClick={() => changeRole(member)}>{member.role === "leader" ? "Make member" : "Make leader"}</Button></ListGroup.Item>)}</ListGroup>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ProjectLeaderManagementModal;
